import { personName } from "@/components/boo/access-copy";
import { BooPair, DecidedLine, LinkStatePill, ScopeChips } from "@/components/grants/GrantBits";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { BooLinkRow } from "@/lib/api";
import { DEPTH_COPY, countLine, expirySoon, linkState, needsApproval, sameScopes, shortDate, sortLinks } from "@/lib/grant-scopes";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

// The one list of Boo-to-Boo grants. The org's access page shows every grant
// in it; a Boo's Sharing tab shows the same rows, only those that Boo is in.

export function GrantList({
  links,
  onOpen,
  empty,
  side = "learner",
  action,
}: {
  links: BooLinkRow[];
  onOpen: (l: BooLinkRow) => void;
  empty: ReactNode;
  /** "source" on the sharing Boo's own list: an active grant reads "Sharing" there. */
  side?: "learner" | "source";
  /** A button at the end of each row (it opens the row too, as a click anywhere does). */
  action?: (l: BooLinkRow) => ReactNode;
}) {
  if (links.length === 0) {
    return <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-pretty text-muted-foreground">{empty}</p>;
  }
  const rows = sortLinks(links);
  const stop = (node: ReactNode) => (
    // The row's own click would open it a second time.
    <span onClick={(e) => e.stopPropagation()} className="contents">
      {node}
    </span>
  );
  return (
    <>
      {/* Phones: one card per grant. */}
      <ul className="flex flex-col divide-y divide-border rounded-lg border border-border md:hidden">
        {rows.map((l) => (
          <li key={l.id} className="flex flex-col">
            <button type="button" onClick={() => onOpen(l)} className="flex w-full flex-col gap-2 px-4 py-3 text-left active:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
              <span className="flex items-start justify-between gap-3">
                <BooPair source={l.source} consumer={l.consumer} className="min-w-0" />
                <Status link={l} side={side} className="items-end" />
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                <HowMuch link={l} />
                <Shared link={l} />
              </span>
              <Approver link={l} />
            </button>
            {action && <span className="px-4 pb-3">{action(l)}</span>}
          </li>
        ))}
      </ul>
      <div className="hidden rounded-lg border border-border md:block">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[32%]">From → To</TableHead>
              <TableHead className="w-[12%]">How much</TableHead>
              <TableHead className="hidden lg:table-cell">What's shared</TableHead>
              <TableHead className="w-[15%]">Status</TableHead>
              <TableHead className="w-[17%]">Approved by</TableHead>
              {action && <TableHead className="w-[10%]">
                <span className="sr-only">Actions</span>
              </TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((l) => (
              // The whole row opens it for a pointer; the button in the first cell does for a keyboard.
              <TableRow key={l.id} className="cursor-pointer" onClick={() => onOpen(l)}>
                <TableCell className="whitespace-normal">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(l);
                    }}
                    aria-label={`Open what ${l.consumer?.name ?? "a deleted Boo"} learns from ${l.source?.name ?? "a deleted Boo"}`}
                    className="-m-1 rounded-md p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <BooPair source={l.source} consumer={l.consumer} />
                  </button>
                </TableCell>
                <TableCell className="whitespace-normal">
                  <HowMuch link={l} />
                </TableCell>
                <TableCell className="hidden whitespace-normal lg:table-cell">
                  <Shared link={l} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <Status link={l} side={side} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <Approver link={l} />
                </TableCell>
                {action && <TableCell className="text-right">{stop(action(l))}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

/** The tier as plain text; nothing while a request for more is open (the row says what's asked). */
function HowMuch({ link }: { link: BooLinkRow }) {
  const depth = link.more ? null : link.status === "approved" ? link.grantedPreset : link.requestedPreset;
  if (!depth || depth === "none") return null;
  return <span className="text-xs">{DEPTH_COPY[depth].label}</span>;
}

/** The state, and when a waiting request runs out once that's close. */
function Status({ link, side, className }: { link: BooLinkRow; side: "learner" | "source"; className?: string }) {
  const expiry = needsApproval(link) ? expirySoon(link.expiresAt) : null;
  return (
    <span className={cn("flex flex-col items-start gap-1", className)}>
      <LinkStatePill state={linkState(link)} side={side} />
      {expiry && <span className="text-xs text-amber-800 dark:text-amber-300">{expiry}</span>}
    </span>
  );
}

function Shared({ link }: { link: BooLinkRow }) {
  // A request for more: what's being asked, not what's already shared.
  if (link.more) {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Asking for:</span>
        <ScopeChips scopes={link.more.scopes} />
      </span>
    );
  }
  const approved = link.status === "approved";
  // In use is everything granted: the link's own skill count is the grant's.
  const skillCount = approved && link.setUp && link.effective && link.granted && sameScopes(link.effective, link.granted) ? link.skills : null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <ScopeChips scopes={approved ? link.granted : link.requested} options={{ skillCount }} />
      {approved && link.setUp && <span className="text-xs tabular-nums text-muted-foreground">In use: {countLine(link)}</span>}
    </span>
  );
}

/** Who approved it; for a first request still open, "Not yet". Who asked and when goes under either while a request is open. */
function Approver({ link }: { link: BooLinkRow }) {
  const asked = (verb: string, by: BooLinkRow["requestedBy"], at: string | null) => (
    <span className="text-muted-foreground [overflow-wrap:anywhere]">
      {verb} {personName(by)}
      {at ? ` · ${shortDate(at)}` : ""}
    </span>
  );
  if (link.status === "requested") {
    return (
      <span className="flex flex-col gap-0.5 text-xs">
        <span>Not yet</span>
        {asked("Asked by", link.requestedBy, link.requestedAt)}
      </span>
    );
  }
  if (!link.decidedAt) return null;
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <DecidedLine link={link} compact />
      {link.more && asked("More asked by", link.more.requestedBy, link.more.requestedAt)}
    </span>
  );
}
